<template>
  <form @submit.prevent="submit" novalidate="true">
    <b-card v-bind:class="{ spinner: isBusy }">
      <!-- Client selection -->
      <template slot="header">
        <div class="form-row">
          <div class="col-md-auto">
            <b-form-radio-group buttons button-variant="outline-primary"
                                v-model="workflowEventTypeValue">
              <b-form-radio :value="WorkflowEventType.CheckReport">{{ $t('Webportal.CheckReport') }}</b-form-radio>
              <b-form-radio :value="WorkflowEventType.CheckRevision">{{ $t('Webportal.Actions.SurveyRevision') }}</b-form-radio>
            </b-form-radio-group>
          </div>
          
          <div class="col-md-6 col-lg">
            <treeselect :auto-load-root-options="false"
                        :placeholder="$t('Webportal.PropertyValueFilter.Placeholder')"
                        :options="propertyItems"
                        :disableFuzzyMatching="true"
                        :multiple="true"
                        :showCount="true"
                        showCountOf="LEAF_DESCENDANTS"
                        :flat="true"
                        :defaultExpandLevel="1"
                        :disable-branch-nodes="true"
                        @input="propertyInput">
            </treeselect>
          </div>

          <div class="col-md-auto ml-auto">
            <div class="input-group d-flex justify-content-end">
              <b-pagination v-model="selectedPage"
                            :total-rows="totalCount"
                            :per-page="pageSize"
                            :limit="5"
                            @change="getClientLocations"
                            class="mb-0"></b-pagination>
                <b-button variant="primary" disabled>
                  {{ $n(this.totalCount) }}
                </b-button>
            </div>
          </div>
        </div>
      </template>

      <div v-if="locations && locations.length > 0">
        <b-table 
          ref="LocationsTable"
          small hover responsive
          :items="locationData"
          :fields="fields"
          sticky-header="400px"
          no-border-collapse
          selectable
          selected-variant="primary"
          no-local-sorting
          @row-selected="onRowSelected">

          <template #head(selected)>
            <b-form-checkbox
              :checked="allRowsSelected"
              @change="allRowsSelected ? $refs.LocationsTable.clearSelected() : $refs.LocationsTable.selectAllRows()" />
          </template>
          <template #head(ClientLocation.ClientLocationIdOnClientSite)>{{ $t('Webportal.LocationId') }}</template>
          <template #head(ClientLocation)>{{ $t('Webportal.LocationAddress') }}</template>

          <template v-slot:cell(selected)="data">
            <b-form-checkbox 
              v-model="data.rowSelected"
              @input="data.rowSelected ? $refs.LocationsTable.selectRow(data.index) : $refs.LocationsTable.unselectRow(data.index)" />
          </template>
          <template #cell(ClientLocation.ClientLocationIdOnClientSite)="data">
            {{ data.value }}<br>
          </template>
          <template #cell(ClientLocation)="data">
            {{ data.value.Name }}, {{ data.value.PostalCode }} {{ data.value.City }}
          </template>
          <template #cell(Notifications)="data">
            <b-badge v-for="notification in data.value" :key="`${notification.NotificationId}_${notification.NotificationAddress}`"
                     :variant="!notification.CanWrite ? 'light' : 'secondary'"
                     class="b-form-tag mr-1" :class="{ 'text-muted': !notification.CanWrite }">
              {{ notification.NotificationAddress }}
              <b-button-close v-if="notification.CanWrite" class="b-form-tag-remove" @click="removeLocationFromNotification(data.item, notification)" />
            </b-badge>
          </template>
        </b-table>

        <hr />

        <b-form-group
          :label-cols-sm="2"
          :label="$t('Webportal.EmailAddress')"
          label-class="font-weight-bold pt-0"
        >
          <b-input-group class="col-md-7 p-0">
            <b-form-input v-model="recipient"
                autocomplete="off"
                v-validate="'required|email'"
                data-vv-name="EMail"
                :state="isRecipientValid"
                @change="onRecipientChanged" />

            <template #append>
              <b-dropdown id="recipient-dropdown" :disabled="recipients.length == 0">
                <b-dropdown-item 
                  v-for="r in recipients"
                  :key="r"
                  @click="recipient = r; onRecipientChanged()">
                  {{ r }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-input-group>
        </b-form-group>

        <b-form-group
          :label-cols-sm="2"
          :label="$t('Webportal.Notification')"
          label-class="font-weight-bold pt-0"
          :disabled="!isRecipientValid"
        >
          <b-form-checkbox-group v-model="currentNotification.Subscription.WorkflowEventIds">
            <template v-if="workflowEventTypeValue == WorkflowEventType.CheckReport">
              <b-form-checkbox :value="1">
                {{ $t("Webportal.NotificationSettings.NotifyOnNewChecks") }}
              </b-form-checkbox>
              <b-form-checkbox :value="2">
                {{ $t("Webportal.NotificationSettings.NotifyOnModifiedChecks") }}
              </b-form-checkbox>
            </template>
            <template v-if="workflowEventTypeValue == WorkflowEventType.CheckRevision">
              <b-form-checkbox :value="11">
                {{ $t("Webportal.NotificationSettings.NotifyOnNewCheckRevisions") }}
              </b-form-checkbox>
              <b-form-checkbox :value="12">
                {{ $t("Webportal.NotificationSettings.NotifyOnModifiedCheckRevisions") }}
              </b-form-checkbox>
            </template>
          </b-form-checkbox-group>
        </b-form-group>

        <b-form-group
          v-if="workflowEventTypeValue == WorkflowEventType.CheckReport && targetGroups.length > 0"
          :label-cols-sm="2"
          :label="$t('Webportal.TargetGroup')"
          label-class="font-weight-bold pt-0"
          :disabled="!isRecipientValid"
        >
          <b-input-group>
            <b-form-select id="TargetGroup"
              class="col-md-7"
              v-model="currentNotification.AdditionalInfo.TargetGroup"
              v-validate data-vv-name="TargetGroup"
              size="sm"
              :disabled="isBusy">
              <b-form-select-option 
                v-for="targetGroup in targetGroups"
                :key="targetGroup.TargetGroup"
                :value="targetGroup.TargetGroup">
                {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupNames.join(' / ') }})
              </b-form-select-option>
            </b-form-select>
            <b-input-group-append>
              <b-btn size="sm"
                    :disabled="isBusy || !currentNotification.AdditionalInfo.TargetGroup"
                    @click="currentNotification.AdditionalInfo.TargetGroup = undefined">
                <font-awesome-icon icon="times" />
              </b-btn>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-form-group
          :label-cols-sm="2"
          :label="$t('Webportal.Schedule')"
          label-class="font-weight-bold pt-0"
          :disabled="!isRecipientValid"
        >
          <schedule-input v-model="currentNotification.CronScheduleUtc" />
        </b-form-group>

        <b-form-group 
          :label-cols-sm="2"
          :disabled="!areNotificationSettingsValid || !selectedLocations || selectedLocations.length == 0">
          <b-button
            variant="primary"
            size="sm"
            @click="applyToSelected">
            <font-awesome-icon :icon="['fas', 'share-all']" /> {{ $t('Webportal.LocationNotifications.ApplyToSelected') }}
          </b-button>

          <b-button
            variant="primary"
            size="sm"
            @click="removeFromSelected">
            <font-awesome-icon :icon="['fas', 'eraser']" /> {{ $t('Webportal.LocationNotifications.RemoveFromSelected') }}
          </b-button>
        </b-form-group>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button
            @click.prevent="cancel"
            class="btn btn-secondary"
            :disabled="!isFormChanged || isBusy"
          >
            <font-awesome-icon :icon="['far', 'ban']" />
            {{ $t("Webportal.Cancel") }}
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="!isFormChanged || isBusy"
          >
            <font-awesome-icon :icon="['far', 'dot-circle']" />
            {{ $t("Webportal.Save") }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import axios from 'axios';
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import ScheduleInput from "@/components/ScheduleInput";
import SurveysService from '@/services/SurveysService';
import AdministationService from '@/services/AdministationService';

const WorkflowEventType = {
  CheckReport: 1,
  CheckRevision: 2
}

const PAGE_SIZE_DEFAULT = 300;

export default {
  name: "LocationNotifications",

  mixins: [AppConfig],

  components: {
    ScheduleInput,
  },

  data() {
    return {
      isBusy: false,
      isFormChanged: false,

      structureItems: [],
      propertyValue: [],
      propertyItems: [],
      WorkflowEventType: WorkflowEventType,
      workflowEventTypeValue: WorkflowEventType.CheckReport,

      // Paging properties
      pageSize: PAGE_SIZE_DEFAULT,          // Number of items on one page of the table
      selectedPage: 1,                      // Number of the page, that is currently visible
      totalCount: 0,                        // Number of total items

      clientLocationsCancelToken: null,
      locations: [],
      selectedLocations: [],

      recipient: '',
      notifications: [],
      changedNotifications: [],
      currentNotification: { 
        CronScheduleUtc: "0 0 17 ? * *",
        CanWrite: true,
        Subscription: {
          WorkflowEventIds: [1],
          ClientLocationIds: []
        },
        AdditionalInfo: {}
      },

      WorkflowEventTypeFilter: notification => 
        this.workflowEventTypeValue == WorkflowEventType.CheckReport && notification.Subscription?.WorkflowEventIds?.some(id => [1, 2].includes(id)) ||
        this.workflowEventTypeValue == WorkflowEventType.CheckRevision && notification.Subscription?.WorkflowEventIds?.some(id => [11, 12].includes(id))
    };
  },

  computed: {
    filter() {
      return  {
        propertyIds: this.propertyValue,
      };
    },

    options() {
      return {
        page: this.selectedPage,
        pageSize: this.pageSize
      }
    },

    fields() {
      return [
        { key: 'selected', label: '', thStyle: 'width:2em' },
        { key: 'ClientLocation.ClientLocationIdOnClientSite', thStyle: 'width:8em' },
        { key: 'ClientLocation', thStyle: '' },
        { key: 'Notifications', label: '', thStyle: 'width:40%' }
      ];
    },

    locationData() {
      return this.locations.map(l =>
        ({ 
          ...l, 
          Notifications: this.notifications.filter(n => 
            this.WorkflowEventTypeFilter(n) &&
            n.Subscription?.ExternalClientLocationIds?.includes(l.ClientLocation.ExternalClientLocationId))
        })
      );
    },

    allRowsSelected() {
      return this.selectedLocations?.length === this.locations.length;
    },

    isRecipientValid() {
      if (this.recipient && this.validationFields["EMail"])
        return this.recipient && this.validationFields["EMail"].valid;
    },

    recipients() {
      return this.notifications
        .filter(this.WorkflowEventTypeFilter)
        .filter(n => n.CanWrite)
        .map(n => n.NotificationAddress)
        .sort();
    },

    areNotificationSettingsValid() {
      return this.isRecipientValid &&
        this.currentNotification.Subscription.WorkflowEventIds.length > 0;
    },

    targetGroups() {
      return _(this.structureItems)
        .flatMapDeep(item => item.id[0] !== 'S' ? item.children : item)
        .flatMapDeep(item => item.id[0] !== 'S' ? item.children : item)
        .filter(item => item && item.id[0] === 'S')
        .flatMapDeep(item => item.TargetGroups)
        .groupBy(item => item.TargetGroup)
        .map((targetGroups) => ({
          TargetGroup: _.first(targetGroups).TargetGroup,
          GroupNames: _.uniq(_.map(targetGroups, item => item.GroupName))
        }))
        .orderBy(item => item.TargetGroup)
        .value();
    }
  },

  watch: {
    currentNotification: {
      handler(newVal, oldVal){
        if (newVal === oldVal)
          this.applyNotificationSettings();
      },
      deep: true
    },

    workflowEventTypeValue() {
      this.onRecipientChanged();
    }
  },

  activated() {
    this.getSurveyStructure();
    this.getSurveyProperties();
    this.getClientLocations();
    this.getNotifications();
  },

  beforeRouteLeave(to, from, next) {
    this.$emit("unsavedChanges", this.isFormChanged, next);
  },

  methods: {
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;
        })
    },

    getSurveyProperties() {
      SurveysService.getSurveyCustomProperties(this.filter, true, false)
        .then(response => {
          this.propertyItems = response.data;
        })
    },

    getClientLocations(page) {
      this.isBusy = true;

      if (page) this.selectedPage = page;

      // Cancel previous request
      if (this.clientLocationsCancelToken) {
        this.clientLocationsCancelToken.cancel();
      }

      this.clientLocationsCancelToken = axios.CancelToken.source();

      AdministationService.getClientLocations(this.filter, this.options, this.clientLocationsCancelToken)
        .then(response => {
          this.selectedLocations = [];
          this.$refs.LocationsTable?.clearSelected();

          this.totalCount = response.data.TotalCount;
          this.locations = response.data.items;
        })
        .finally(_ => {
          this.isBusy = false;
        });
    },

    getNotifications() {
      this.isBusy = true;

      AdministationService.getNotifications(false, true)
        .then(response => {
          this.notifications = response.data;
          this.changedNotifications = [];
          this.onRecipientChanged();
          this.isFormChanged = false;
        })
        .finally(_ => {
          this.isBusy = false;
        });
    },

    propertyInput(value) {
      if (this.propertyValue !== value)
      {
        let currentValue = this.propertyValue;
        this.propertyValue = value;
        
        this.$emit('unsavedChanges', this.isFormChanged, result => {
          if (result === false)
          {
            this.propertyValue = currentValue;
          }
          else
            this.getClientLocations();
        });
      }
    },

    onRowSelected(items) {
      this.selectedLocations = items;
    },

    onRecipientChanged() {
      this.currentNotification = 
        this.notifications.find(n => this.WorkflowEventTypeFilter(n) && n.NotificationAddress.localeCompare(this.recipient, undefined, { sensitivity: 'accent' }) == 0 && n.CanWrite) ?? {
          Name: this.workflowEventTypeValue == WorkflowEventType.CheckReport ? 'Check Reports' : 'Check Revisions',
          NotificationTypeId: 1,
          NotificationAddress: this.recipient,
          CronScheduleUtc: this.currentNotification.CronScheduleUtc,
          CanWrite: true,
          Subscription: { 
            WorkflowEventIds: [ this.workflowEventTypeValue == WorkflowEventType.CheckReport ? 1 : 11 ],
            ExternalClientLocationIds: []
          },
          AdditionalInfo: {}
        };
    },

    applyNotificationSettings() {
      if (!this.notifications.includes(this.currentNotification))
        this.notifications.push(this.currentNotification);

      if (!this.changedNotifications.includes(this.currentNotification))
        this.changedNotifications.push(this.currentNotification);

      this.isFormChanged = true;
    },

    addLocationToNotification(location, notification) {
      if (!notification.Subscription.ExternalClientLocationIds.includes(
        location.ClientLocation.ExternalClientLocationId))
      {
        notification.Subscription.ExternalClientLocationIds.push(
          location.ClientLocation.ExternalClientLocationId
        );
        this.isFormChanged = true;
      }
    },

    removeLocationFromNotification(location, notification) {
      if (!this.changedNotifications.includes(notification))
        this.changedNotifications.push(notification);

      let index = notification.Subscription.ExternalClientLocationIds.findIndex(
          e => e == location.ClientLocation.ExternalClientLocationId);

      if (index > -1)
      {
        notification.Subscription.ExternalClientLocationIds.splice(index, 1);
        this.isFormChanged = true;
      }
    },

    applyToSelected() {
      this.applyNotificationSettings();

      this.selectedLocations.forEach(l => {
        this.addLocationToNotification(l, this.currentNotification);
      });

      this.selectedLocations = [];
      this.$refs.LocationsTable.clearSelected();
    },

    removeFromSelected() {
      this.selectedLocations.forEach(l => {
        this.removeLocationFromNotification(l, this.currentNotification);
      });

      this.selectedLocations = [];
      this.$refs.LocationsTable.clearSelected();
    },

    cancel() {
      this.$emit("unsavedChanges", this.isFormChanged, (result) => {
        if (result !== false) this.getNotifications();
      });
    },

    async submit() {
      this.isBusy = true;

      try {
        await AdministationService.setNotifications(this.changedNotifications);

        this.getNotifications();
      } catch (error) {
        console.error(error);

        this.$bvToast.toast(
          this.$t("Webportal.SaveFailed.Text", {
            entity: this.$t("Webportal.LocationNotifications"),
          }),
          {
            autoHideDelay: 10 * 1000,
            variant: "danger",
            noCloseButton: true,
          }
        );
        throw error;
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style>
  #recipient-dropdown .dropdown-menu {
      max-height: 400px;
      overflow-y: auto;
  }
</style>